import { generate as generateUniqueName } from 'randomstring'
import client from 'configs/apolloClient'
import { GET_SIGNED_URL } from 'queries/common'
import { replaceS3UrlsWithCdn } from 'utils/replaceS3UrlsWithCdn'
import { getSignedUrls, getSignedUrlsVariables } from '__generatedTypes__/getSignedUrls'

/**
 * Convert file dataUrl (e.g. "data:image/png;base64,iVBORw0KGgoAAA....ANS") to File
 */
export const dataUrlToFile = async (dataUrl: string, name?: string) => {
  const matchResult = dataUrl.match(/data:(\S*?)\/(\S*?);/)

  if (!matchResult) {
    throw new Error('Wrong dataUrl')
  }

  const [, fileType, fileExtension] = matchResult
  const fileName = name || generateUniqueName()
  const fullFileName = `${fileName}.${fileExtension}`

  return fetch(dataUrl)
    .then((res) => res.blob())
    .then((blob) => {
      const file = new File([blob], fileName, { type: `${fileType}/${fileExtension}` })

      return {
        dataUrl,
        fileName: fullFileName,
        file,
      }
    })
}

/**
 * Receive file signed url
 */
export const getSignedUrl = async ({ file, fileName, pathS3 }: { file: File; fileName: string; pathS3: string }) => {
  const { data } = await client.query<getSignedUrls, getSignedUrlsVariables>({
    query: GET_SIGNED_URL,
    variables: {
      query: {
        contentType: file.type,
        fileName: fileName,
        method: 'putObject',
        path: pathS3,
      },
    },
  })

  return data.getSignedUrls
}

/**
 * Upload file to signed url
 */
export const saveFile = async ({
  file,
  fileName,
  fileSignedUrl,
}: {
  file: File
  fileName: string
  fileSignedUrl: string
}) => {
  await fetch(fileSignedUrl, {
    method: 'PUT',
    headers: {
      'Content-Type': file.type,
      'Content-Disposition': `attachment; filename="${fileName}"`,
    },
    body: file,
  })
}

/**
 * Upload base64 image to s3 path
 */
export const saveBase64Image = async (dataUrl: string, pathS3: string) => {
  const { fileName, file } = await dataUrlToFile(dataUrl)
  const fileSignedUrl = await getSignedUrl({ file, fileName, pathS3 })
  await saveFile({ file, fileName, fileSignedUrl })
  const fileUrl = `${process.env.REACT_APP_IMAGEKIT_URL}/tr:h-default,w-default/${pathS3}/${fileName}`

  return {
    dataUrl,
    fileName,
    fileUrl,
    file,
  }
}

/**
 * Find all base64 images in the HTML text.
 * Matches any base64 image data in double quotes, including:
 * - <img src="data:image/...">
 * - <span data-preview-image="data:image/..."> (PreviewImage plugin)
 */
const findAllBase64Images = (text: string) =>
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  [...text.matchAll(/"(data:image\/\S*?)"/g)].map(([, dataUrl]) => dataUrl)

const replaceAllBase64Images = (text: string, images: Array<{ dataUrl: string; fileUrl: string }>) =>
  images.reduce((text, { dataUrl, fileUrl }) => text.replaceAll(dataUrl, fileUrl), text)

const trimImgSrcAttributes = (html: string) =>
  html.replace(/<img\b[^>]*>/gi, (imgTag) =>
    imgTag
      .replace(/\ssrc="([^"]*)"/gi, (_, url) => ` src="${url.trim()}"`)
      .replace(/\ssrc='([^']*)'/gi, (_, url) => ` src='${url.trim()}'`)
  )

const trimPreviewImageAttributes = (html: string) =>
  html
    .replace(/\sdata-preview-image="([^"]*)"/gi, (_, url) => ` data-preview-image="${url.trim()}"`)
    .replace(/\sdata-preview-image='([^']*)'/gi, (_, url) => ` data-preview-image='${url.trim()}'`)

/**
 * Trim leading/trailing whitespace in image URL attributes (e.g. from pasted HTML).
 */
const trimImageUrlsInHtml = (html: string): string => {
  if (!html) {
    return html
  }

  const trimmedImagesHtml = trimImgSrcAttributes(html)

  return trimPreviewImageAttributes(trimmedImagesHtml)
}

/**
 * Prepare editor HTML for save: upload base64 images to S3, normalize image URLs
 * and replace direct S3 URLs with CDN ones.
 */
export const prepareEditorHtml = async (textAsHtml: string, pathS3: string) => {
  try {
    const base64Images: string[] = Array.from(new Set(findAllBase64Images(textAsHtml)))

    const images = await Promise.all(base64Images.map((dataUrl) => saveBase64Image(dataUrl, pathS3)))

    const replacedBase64ImagesHtml = replaceAllBase64Images(textAsHtml, images)

    return replaceS3UrlsWithCdn(trimImageUrlsInHtml(replacedBase64ImagesHtml))
  } catch (e) {
    console.error('prepareEditorHtml failed:', e)
    return replaceS3UrlsWithCdn(trimImageUrlsInHtml(textAsHtml))
  }
}
